import { createContext, useContext, useState, useEffect, ReactNode } from "react";
import Cookies from "js-cookie";
import { useNavigate } from "react-router-dom";
import api from "../libs/axios";
import { Attendance } from "../types/attendance";
import { getTodayAttendance } from "../libs/attendance";
import { removeFcmToken } from "../libs/notifications";

interface Permission {
    id: number;
    name: string;
    resource: string;
    action: string;
    description: string;
}

interface RolePermission {
    permission_id: number;
    permission: Permission;
}

interface User {
    id: number;
    email: string;
    username: string;
    first_name: string;
    last_name: string;
    role: {
        id: number;
        name: string;
        level: number;
        role_permissions: RolePermission[];
    };
    phone_number?: string | null;
    profile_picture?: string | null;
    country_code?: string | null;
    date_of_birth?: string | null;
    gender?: string | null;
    address?: string | null;
    city?: string | null;
    state?: string | null;
    country?: string | null;
    pincode?: string | null;
    status?: string;
    created_at?: string;
    role_id?: number;
    // Whether the user is ESPL staff or belongs to a vendor.
    association_type?: 'inhouse' | 'vendor';
    vendor_id?: number | null;
}

// Report maker. Kept here rather than inlined so the rule below reads clearly.
const REPORT_MAKER_ROLE_ID = 9;

interface AuthContextType {
    user: User | null;
    token: string | null;
    loading: boolean;
    attendance: Attendance | null;
    onLeave: boolean;
    login: (token: string, rememberMe?: boolean) => Promise<void>;
    logout: () => void;
    fetchProfile: () => Promise<void>;
    fetchAttendance: () => Promise<void>;
    hasPermission: (resource: string, action: string) => boolean;
    canAccessRoute: (path: string) => boolean;
    // True for a report maker who belongs to a vendor. Those users are an
    // outside party: they get the survey data but never the identity of the
    // site engineer who did the field work. The server already strips it from
    // every response; this flag lets screens drop the empty column entirely.
    hidesSiteEngineer: boolean;
}

// Route permissions mapping
const ROUTE_PERMISSIONS = {
    // '/dashboard': { resource: 'Dashboard', action: 'view' }, // Assuming everyone can view dashboard
    '/team': { resource: 'Team', action: 'view' },
    '/clients': { resource: 'Client', action: 'view' },
    '/projects': { resource: 'Project', action: 'view' },
    '/roles': { resource: 'Role', action: 'view' },
    '/states': { resource: 'StateCircle', action: 'view' },
    '/activities': { resource: 'Activity', action: 'view' },
    '/materials': { resource: 'MaterialWarehouse', action: 'view_all' },
};

// Creating the AuthContext
const AuthContext = createContext<AuthContextType | undefined>(undefined);

// AuthProvider component
const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
    const [loading, setLoading] = useState(true);
    const [user, setUser] = useState<User | null>(null);
    const [token, setToken] = useState<string | null>(Cookies.get("auth_token") || null);
    const [attendance, setAttendance] = useState<Attendance | null>(null);
    const [onLeave, setOnLeave] = useState<boolean>(false);

    const navigate = useNavigate();

    const fetchAttendance = async () => {
        try {
            const data = await getTodayAttendance();
            if (data.success) {
                setAttendance(data.data.attendance);
                setOnLeave(data.data.on_leave);
            }
        } catch (error) {
            console.error("Error fetching attendance:", error);
        }
    };

    const fetchProfile = async () => {
        setLoading(true);
        try {
            const response = await api.get("/auth/profile");
            // API shape: { success, message, data: { user: {...} }, statusCode }
            const apiUser = response?.data?.data?.user;
            if (apiUser) {
                setUser(apiUser);
                await fetchAttendance();
            }
        } catch (error) {
            console.error("Error fetching profile:", error);
            // If profile fetch fails, logout user
            logout();
        } finally {
            setLoading(false);
        }
    };

    // Strict permissions: level=1 bypass does NOT apply to these.
    // These must be explicitly assigned in the Roles page per user.
    const STRICT_PERMISSIONS = [
        'req_view_project',
        'req_view_activity',
        'req_view_common',
    ];

    const hasPermission = (resource: string, action: string): boolean => {
        if (!user || !user.role || !user.role.role_permissions) {
            return false;
        }
        // Level 1 (Super Admin/Admin) bypasses all checks except strict requirement permissions
        if (user.role.level === 1 && !STRICT_PERMISSIONS.includes(action)) {
            return true;
        }
        return user.role.role_permissions.some(rolePermission => 
            rolePermission.permission.resource === resource && 
            rolePermission.permission.action === action
        );
    };

    // Check if user can access a specific route
    const canAccessRoute = (path: string): boolean => {
        const routePermission = ROUTE_PERMISSIONS[path as keyof typeof ROUTE_PERMISSIONS];
        
        // If no permission is defined for the route, allow access (like dashboard)
        if (!routePermission) {
            return true;
        }

        return hasPermission(routePermission.resource, routePermission.action);
    };

    // Fetch user profile if token exists
    useEffect(() => {
        if (token) {
            fetchProfile();
        } else {
            setLoading(false);
        }
    }, [token]);

    // Login function
    const login = async (token: string, rememberMe?: boolean) => {
        try {
            Cookies.set("auth_token", token, rememberMe ? { expires: 7 } : undefined);
            setToken(token);
        } catch (error: any) {
            if (error.response?.status === 403) {
                console.error("API Error: 403 - Forbidden");
            } else {
                console.error("Login failed:", error);
            }
        }
    };

    // Logout function
    const logout = async () => {
        try {
            // TODO: Uncomment when notification backend is ready
            try {
                await removeFcmToken();
            } catch (fcmError) {
                console.error("Error removing FCM token:", fcmError);
            }
            await api.post('/auth/logout');
        } catch (error) {
            console.error("Logout API error:", error);
        } finally {
            Cookies.remove("auth_token");
            setToken(null);
            setUser(null);
            navigate("/login");
        }
    };

    // Auto-logout on inactivity: if the user has no interaction for more than
    // 1 hour, log them out of the web session.
    useEffect(() => {
        if (!token) return;

        const IDLE_TIMEOUT_MS = 60 * 60 * 1000; // 1 hour
        let idleTimer: ReturnType<typeof setTimeout>;

        const handleIdleLogout = () => {
            console.warn("Session expired due to inactivity. Logging out.");
            logout();
        };

        const resetIdleTimer = () => {
            clearTimeout(idleTimer);
            idleTimer = setTimeout(handleIdleLogout, IDLE_TIMEOUT_MS);
        };

        const activityEvents: (keyof WindowEventMap)[] = [
            "mousemove",
            "mousedown",
            "keydown",
            "scroll",
            "touchstart",
            "click",
            "wheel",
        ];

        activityEvents.forEach((eventName) =>
            window.addEventListener(eventName, resetIdleTimer, { passive: true })
        );

        // Start the timer immediately on mount / login
        resetIdleTimer();

        return () => {
            clearTimeout(idleTimer);
            activityEvents.forEach((eventName) =>
                window.removeEventListener(eventName, resetIdleTimer)
            );
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [token]);

    return (
        <AuthContext.Provider value={{ 
            user, 
            token, 
            loading, 
            attendance,
            onLeave,
            login, 
            logout, 
            fetchProfile, 
            fetchAttendance,
            hasPermission,
            canAccessRoute,
            hidesSiteEngineer:
                Number(user?.role_id ?? user?.role?.id) === REPORT_MAKER_ROLE_ID &&
                String(user?.association_type || "").toLowerCase() === "vendor",
        }}>
            {loading ? (
                <div className="flex min-h-screen items-center justify-center">
                    <div className="animate-spin rounded-full border-b-2 border-blue-600 h-8 w-8"></div>
                </div>
            ) : (
                children
            )}
        </AuthContext.Provider>
    );
};

// Custom hook to use AuthContext
export const useAuth = () => {
    const context = useContext(AuthContext);
    if (!context) {
        throw new Error("useAuth must be used within an AuthProvider");
    }
    return context;
};

export default AuthProvider;
